import { between, maxValue, minValue, required } from 'vuelidate/lib/validators';
import { IAsphaltDesignImperial } from '@/modules/design/interfaces/newAsphalt';
import { DesignScenarioType, EsalCalculationMethods } from '@/modules/design/interfaces/shared';
import { BaseScenarioType } from '@/lib/types/IBaseScenario';
import { RoadwayClassifications } from '@/lib/data/roadwayClassification';

export const createAsphaltValidations = (scenario: IAsphaltDesignImperial) => {
  const validationObject = {
    scenarioImperial: {
      // Page 1
      name: {
        required,
      },
      completionYear: {
        minValue: minValue(new Date(Date.now()).getFullYear() - 100),
        maxValue:
          maxValue(new Date(Date.now()).getFullYear() + 100),
      },
      roadwayClassification: {
        required,
      },

      // Page 2
      designPeriod_years: {
        required,
        minValue: minValue(0),
      },
      reliabilityLevel: {
        required,
      },
      standardError: {
        required,
        between: between(0, 1),
      },
      initialServiceability: {
        required,
        between: between(0, 5),
      },
      terminalServiceability: {
        required,
        between: between(0, 5),
      },


      // Page 3
      initialTraffic: {
        required,
        minValue: minValue(0),
      },
      loadEquivalencyFactor: {
        required,
        minValue: minValue(0),
      },
      esals: {
        required,
        minValue: minValue(0),
      },
      trafficGrowthRate: {
        required,
      },
      esalGrowthRate: {
        required,
      },
      totalDesignEsals: {
        required,
        minValue: minValue(0),
      },
      loadDiameter_in: {
        required,
        minValue: minValue(0),
      },


      // Page 4
      layerCoefficient: {
        required,
        between: between(0, 1),
      },
      drainageCoefficient: {
        required,
        between: between(0, 2),
      },
      minimumThickness: {
        required,
        minValue: minValue(0),
      },

      singleLiftGroup: ['scenarioImperial.layerCoefficient', 'scenarioImperial.drainageCoefficient', 'scenarioImperial.minimumThickness'],

      surfaceLayers: {
        0: {
          coefficient: {
            required,
            between: between(0, 1),
          },
          drainage: {
            required,
            between: between(0, 2),
          },
          thickness: {
            required,
            minValue: minValue(0.1),
          },
        },
        1: {
          coefficient: {
            required,
            between: between(0, 1),
          },
          drainage: {
            required,
            between: between(0, 2),
          },
          thickness: {
            required,
            minValue: minValue(0.1),
          },
        },
        2: {
          coefficient: {
            required,
            between: between(0, 1),
          },
          drainage: {
            required,
            between: between(0, 2),
          },
          thickness: {
            // required,
            // minValue: minValue(0.1),
          },
        },

      },

      substructureLayers: (function () {
        // Create a validation for each available layer
        const layerMap: { [index: number]: any } = {};
        scenario.substructureLayers.forEach((layer, i) => {
          if (layer.id) {
            layerMap[i] = {
              coefficient: {
                required,
                minValue: minValue(0),
              },
              drainage: {
                required,
                minValue: minValue(0),
              },
              thickness: {
                required,
                minValue: minValue(0),
              },
              modulus: {
                required,
                minValue: minValue(0),
              },
            };
          }
        });

        return layerMap;
      }()),

      // Page 5
      subgrade: {
        required,
        minValue: minValue(0),
      },


    },

    /*
    Top level validations - not on model directly
     */

    deltaServiceability: {
      between: between(0, 5),
    },

    // Page 2

    aadtGroup: [
      'scenarioImperial.initialTraffic',
      'scenarioImperial.loadEquivalencyFactor',
      'scenarioImperial.esals',
      'scenarioImperial.trafficGrowthRate',
      'scenarioImperial.esalGrowthRate',
      'scenarioImperial.totalDesignEsals',
    ],
    annualEsalGroup: [
      'scenarioImperial.esals',
      'scenarioImperial.esalGrowthRate',
      'scenarioImperial.totalDesignEsals',
    ],
    designEsalGroup: [
      'scenarioImperial.totalDesignEsals',
    ],


    1: [
      'scenarioImperial.name',
      'scenarioImperial.completionYear',
      'scenarioImperial.roadwayClassification',
    ],
    2: [
      'scenarioImperial.designPeriod_years',
      'scenarioImperial.reliabilityLevel',
      'scenarioImperial.standardError',
      'scenarioImperial.initialServiceability',
      'scenarioImperial.terminalServiceability',
      'deltaServiceability',
    ],
    3: (function () {
      // Validate based on method of calculation and whether or not it's porous (extra param)

      if (scenario.esalCalculatedFrom === EsalCalculationMethods.aadt) {
        return [
          'scenarioImperial.initialTraffic',
          'scenarioImperial.loadEquivalencyFactor',
          'scenarioImperial.esals',
          'scenarioImperial.trafficGrowthRate',
          'scenarioImperial.esalGrowthRate',
          'scenarioImperial.totalDesignEsals',
        ];
      }
      if (scenario.esalCalculatedFrom === EsalCalculationMethods.annual) {
        return [
          'scenarioImperial.esals',
          'scenarioImperial.esalGrowthRate',
          'scenarioImperial.totalDesignEsals',
        ];
      }
      return [
        'scenarioImperial.totalDesignEsals',
      ];
    }()),
    4: (function () {
      if (!scenario.multipleLifts) {
        return [
          'scenarioImperial.layerCoefficient',
          'scenarioImperial.drainageCoefficient',
          'scenarioImperial.minimumThickness',
        ];
      }
      return [
        'scenarioImperial.surfaceLayers.0.coefficient',
        'scenarioImperial.surfaceLayers.0.drainage',
        'scenarioImperial.surfaceLayers.0.thickness',

        'scenarioImperial.surfaceLayers.1.coefficient',
        'scenarioImperial.surfaceLayers.1.drainage',
        'scenarioImperial.surfaceLayers.1.thickness',

        'scenarioImperial.surfaceLayers.2.coefficient',
        'scenarioImperial.surfaceLayers.2.drainage',
        'scenarioImperial.surfaceLayers.2.thickness',
      ];

      // 'scenarioImperial.singleLiftGroup',
    }()),
    5: [
      'scenarioImperial.substructureLayers',
      'scenarioImperial.subgrade',
    ],
    6: (function () {
      // Don't validate intermediate pages if it's a light parking lot 
      if (scenario.scenarioType === BaseScenarioType.design
        && scenario.designScenarioType === DesignScenarioType.asphalt
        && scenario.roadwayClassification === RoadwayClassifications.parkingLotLight
      ) {
        return ['1'];
      }

      return [
        '1',
        '2',
        '3',
        '4',
        '5',
      ];
    }()),


    // aadtGroupPorous: [
    //   'scenarioImperial.initialTraffic',
    //   'scenarioImperial.loadEquivalencyFactor',
    //   'scenarioImperial.esals',
    //   'scenarioImperial.trafficGrowthRate',
    //   'scenarioImperial.esalGrowthRate',
    //   'scenarioImperial.totalDesignEsals',
    //   'scenarioImperial.loadDiameter_in'
    // ],
    // annualEsalGroupPorous: [
    //   'scenarioImperial.esals',
    //   'scenarioImperial.esalGrowthRate',
    //   'scenarioImperial.totalDesignEsals',
    //   'scenarioImperial.loadDiameter_in'
    // ],
    // designEsalGroupPorous: [
    //   'scenarioImperial.totalDesignEsals',
    //   'scenarioImperial.loadDiameter_in'
    // ],

  };
  return validationObject;
};
